import React, { useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'

import { 
  Button, 
  Form, 
  Input, 
  notification, 
  Space, 
  Typography 
} from 'antd'

import { CheckCircleOutlined } from '@ant-design/icons'
import { CustomModal } from '@components'
import { JOIN_WAIT_LIST_MUTATION, LEAVE_WAIT_LIST_MUTATION } from '@graphql'

import PropTypes from 'prop-types'

import './WaitListModal.less'

const { Paragraph, Title } = Typography

const WaitListModal = ({ 
  id = null,
  initialComment = '', 
  event, 
  guardian, 
  handleCancel, 
  invitationGroup, 
  mode = "new" 
}) => {
  const initialMode = useRef(mode)
  const [comment, setComment] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    setComment(initialComment || '')
  }, [initialComment])

  const [leaveWaitlist, { loading: removing }] = useMutation(LEAVE_WAIT_LIST_MUTATION, {
     onError: error => {
      notification.error({
        message: 'Error',
        description: `There was an error leaving the waiting list. Please try again.`
      })
    },

    onCompleted: () => setSuccessMessage({
      title: 'Success!',
      description: 'You have successfully left the waiting list for this group.'
    }),

    update: (cache, { data: { leaveWaitingList: deletedId } }) => {
      cache.modify({
        id: cache.identify({ __typename: 'Event', id: event.id }),
        fields: {
          waitingList(existingWaitingList = [], { readField }) {
            return existingWaitingList.filter(entry => readField('id', entry) !== deletedId)
          }
        }
      })
    }
  })

  const [joinWaitlist, { loading }] = useMutation(JOIN_WAIT_LIST_MUTATION, {
     onError: error => {
      notification.error({
        message: 'Error',
        description: `There was an error joining the waiting list. Please try again.`
      })
    },

    onCompleted: () => {
      if (initialMode.current === "new") {
        setSuccessMessage({
          title: 'Success!',
          description: 'You have successfully joined the waiting list for this group. If a slot becomes available, the administrator may book this slot for you.'
        })
      } else {
        handleCancel()
      }
    },

    update: (cache, { data: { joinWaitingList } }) => {
      cache.modify({
        id: cache.identify({ __typename: 'Event', id: event.id }),
        fields: {
          waitingList(existingWaitingList = [], { readField, toReference }) {
            const newRef = toReference(joinWaitingList)
            const exists = existingWaitingList.some(entry =>
              String(readField('id', entry)) === String(joinWaitingList.id)
            )

            return exists
              ? existingWaitingList.map(entry =>
                  String(readField('id', entry)) === String(joinWaitingList.id)
                    ? newRef
                    : entry
                )
              : [...existingWaitingList, newRef]
          }
        }
      })
    }
  })

  const handleSave = () => {    
    joinWaitlist({
      variables: {
        input: {
          event_id: event.id,
          group_id: invitationGroup.group.id,
          lesson_id: invitationGroup.lesson.id,
          student_id: invitationGroup.student.id,
          guardian_id: guardian.id,
          comment: comment
        }      
      }
    })
  }

  const CancelButton = () => (
    <Button onClick={() => handleCancel()} size="large" shape='round' type="default">
      Cancel
    </Button>
  )

  const DeleteButton = () => (
    <Button onClick={() => leaveWaitlist({ variables: { input: { id: id } } })} size="large" shape='round' type="primary" danger loading={removing}>
      Leave Waiting List
    </Button>
  )

  const isDirty = comment !== initialComment
  const modalTitle = mode === "edit" 
    ? `Manage Waiting List request for ${invitationGroup?.group?.title}` 
    : `Join Waiting List for ${invitationGroup?.group?.title}`
  
    if (successMessage) {
      return (
        <CustomModal className='sb-waitlist-modal__success' width='100%' visible={true} closable={false} footer={null}>
          <CheckCircleOutlined className='sb-waitlist-modal__success__icon' />
          <Title level={3}>{successMessage.title}</Title>
          <Paragraph className='sb-waitlist-modal__success__description'>
            {successMessage.description}
          </Paragraph>

          <Button className='sb-waitlist-modal__success__close-button' onClick={() => handleCancel()} type='primary' shape='round' size="large">
            Close
          </Button>
        </CustomModal>
      )
    }

  return (
    <CustomModal
      className='sb-waitlist-modal'
      title={<Title level={4} style={{ marginBottom: 0 }}>{modalTitle}</Title>}
      closable
      visible={true}
      onCancel={handleCancel}
      width='100%'
      footer={null}
    >
      <div className='sb-waitlist-modal__introduction'>
        {mode === "new" && (
          <Paragraph className='sb-waitlist-modal__introduction-text'>
            This group is currently fully booked. Join the waiting list and the school may assign you a slot if one becomes available.
          </Paragraph>
        )}

        {mode === "edit" && (
          <div className="sb-waitlist-modal__manage">
            <CheckCircleOutlined /> 
            
            <Paragraph className='sb-waitlist-modal__introduction-text'>
              You are currently on the waiting list for this group. You can update your comments or leave the waiting list.
            </Paragraph>
          </div>
        )}

        <Paragraph className='sb-waitlist-modal__comment-label'>
          Please add any comments such as times available etc.      
        </Paragraph>
      </div>

      <div className='sb-comments-modal__form'>
        <Form id='comments-form'>
          <Input.TextArea
            style={{ borderRadius: '20px' }}
            value={comment}
            onChange={e => setComment(e.target.value)}
            autoFocus
            rows={6}
            showCount
            maxLength={250}
          />
        </Form>

        <Space style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '16px', width: '100%' }}>
          {mode === "edit" ? <DeleteButton /> : <CancelButton />}

          <Button
            onClick={() => handleSave()}
            type='primary'
            shape='round'
            size='large'
            loading={loading}
            disabled={!isDirty || comment.trim() === ''}
          >
            {mode === "edit" ? "Save Comment" : "Join Waiting List"}
          </Button>
        </Space>
      </div>
    </CustomModal>
  )
}


WaitListModal.propTypes = {
  event: PropTypes.object.isRequired,
  guardian: PropTypes.object.isRequired,
  id: PropTypes.string,
  initialComment: PropTypes.string,
  invitationGroup: PropTypes.object.isRequired,
  handleCancel: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['new', 'edit'])
}

export default WaitListModal
